import { useState, useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight, CalendarDays } from "lucide-react";
import { Input } from "./ui/input";

// N1 Group A — one shared date picker. Stores dates in the canonical machine format
// (YYYY-MM-DD) ONLY; the weekday appears only in the human preview, never in the value.
// Weekend blocking is a prop; when ON it rejects typed weekend dates as well as clicks.
// An already-stored weekend value stays visible and editable and is never auto-cleared.
const WD = ["S", "M", "T", "W", "T", "F", "S"];
const MON = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");
const iso = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const parse = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((s || "").trim()); return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null; };
const isWeekend = (dt) => !!dt && (dt.getDay() === 0 || dt.getDay() === 6);
const humanLabel = (dt) => dt ? `${DOW[dt.getDay()]} ${dt.getDate()} ${MON[dt.getMonth()]} ${dt.getFullYear()}` : "";

export function SmartDatePicker({ value, onChange, blockWeekends = false, testid = "date", className = "" }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(value || "");
  const [err, setErr] = useState("");
  const ref = useRef(null);
  const sel = parse(value);
  const [view, setView] = useState(() => sel || new Date());

  useEffect(() => { setText(value || ""); }, [value]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const commit = (s) => {
    if (!s) { setErr(""); onChange(""); return; }
    const dt = parse(s);
    if (!dt) { setErr("Use YYYY-MM-DD"); return; }
    if (blockWeekends && isWeekend(dt)) { setErr("Weekends are not allowed here"); return; }
    setErr(""); onChange(s);
  };
  const pick = (y, m, d) => {
    if (blockWeekends && isWeekend(new Date(y, m, d))) return;
    const s = iso(y, m, d); setText(s); commit(s); setOpen(false);
  };

  const y = view.getFullYear(), m = view.getMonth();
  const first = new Date(y, m, 1).getDay();
  const days = new Date(y, m + 1, 0).getDate();
  const t = new Date(); const tISO = iso(t.getFullYear(), t.getMonth(), t.getDate());
  const cells = []; for (let i = 0; i < first; i++) cells.push(null); for (let d = 1; d <= days; d++) cells.push(d);

  return (
    <div className={`relative ${className}`} ref={ref} data-testid={`${testid}-wrap`}>
      <div className="flex items-center gap-1">
        <Input value={text} placeholder="YYYY-MM-DD" data-testid={testid}
          onChange={(e) => setText(e.target.value)}
          onBlur={() => commit(text.trim())}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(text.trim()); } }}
          className="h-7 text-xs" />
        <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}
          className="h-7 px-1.5 border border-slate-200 rounded text-slate-500 hover:bg-slate-50"><CalendarDays className="w-3.5 h-3.5" /></button>
      </div>
      {sel && <div className="text-[10px] text-slate-500 mt-0.5" data-testid={`${testid}-preview`}>{humanLabel(sel)}{blockWeekends && isWeekend(sel) ? " · stored weekend date" : ""}</div>}
      {err && <div className="text-[10px] text-rose-600 mt-0.5" data-testid={`${testid}-error`}>{err}</div>}
      {open && (
        <div className="absolute z-50 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg p-2 w-56" data-testid={`${testid}-popover`}>
          <div className="flex items-center justify-between mb-1">
            <button type="button" onClick={() => setView(new Date(y, m - 1, 1))} data-testid={`${testid}-prev`}><ChevronLeft className="w-4 h-4" /></button>
            <span className="text-xs font-semibold" data-testid={`${testid}-month`}>{MON[m]} {y}</span>
            <button type="button" onClick={() => setView(new Date(y, m + 1, 1))} data-testid={`${testid}-next`}><ChevronRight className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-7 gap-0.5 text-[10px] text-slate-400 mb-0.5">{WD.map((w, i) => <div key={i} className="text-center">{w}</div>)}</div>
          <div className="grid grid-cols-7 gap-0.5">
            {cells.map((d, i) => {
              if (!d) return <div key={`e${i}`} />;
              const cISO = iso(y, m, d); const disabled = blockWeekends && isWeekend(new Date(y, m, d));
              const isSel = cISO === value; const isToday = cISO === tISO;
              return (
                <button key={cISO} type="button" disabled={disabled} onClick={() => pick(y, m, d)}
                  data-testid={`${testid}-day-${cISO}`}
                  className={`h-6 text-[11px] rounded ${disabled ? "text-slate-300 cursor-not-allowed" : "hover:bg-slate-100"} ${isSel ? "bg-slate-900 text-white" : ""} ${isToday && !isSel ? "ring-1 ring-slate-400" : ""}`}>{d}</button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
export default SmartDatePicker;
